import { APP_ROUTES } from "config";
import { produce } from "immer";
import { Store } from "reducers/rootReducer";

/* STATE */

export type NestedFormModuleData = {
  selectedId?: number;
  actionType: "create" | "edit" | "view";
  module: APP_ROUTES;
  // createdDataSearchString?: string; // Search string of name of newly creatd item from submit function to put in parent form search bar
  createdDataSearchStringFieldName?: string; // Field in nested form to get the search string from
  createdDataParentFieldName?: string; // Field in parent form to search with newly created item
};

export type CreatedDataParentFieldDataItem = {
  fieldName: string;
  searchString: string;
  id?: string | number;
};

export type CreatedDataParentFieldData = {
  //key is parentModuleName
  [key in APP_ROUTES]?: CreatedDataParentFieldDataItem[];
};

export type NestedParentFormData = {
  [key in APP_ROUTES]?: {
    [fieldName: string]: any;
  };
};

type State = {
  closeNestedMenu: boolean;
  nestedFormModuleData: NestedFormModuleData | null;
  createdDataParentFieldData: CreatedDataParentFieldData | null;
  reloadNestedFormParentFormToggle: boolean;
  nestedParentFormData: NestedParentFormData | null;

  /**
   *  Store parent context for nested form creation
   * @property parentModuleName - Module name of the parent form
   * @property parentRecordId - Record ID of the parent form
   * @property parentFieldNameInChild - Field name in CHILD form that links to parent (e.g., "sitesId")
   * @property targetModule - Module name of the nested form being created
   */
  nestedFormCreationContext: {
    parentModuleName: APP_ROUTES | null;
    parentRecordId: number | null;
    parentFieldNameInChild: string | null;
    targetModule: APP_ROUTES | null;
  } | null;
};

const initialState: State = {
  closeNestedMenu: false,
  nestedFormModuleData: null,
  createdDataParentFieldData: null,
  reloadNestedFormParentFormToggle: false,
  nestedParentFormData: null,
  nestedFormCreationContext: null,
};

/* ACTIONS */

const CLOSE_NESTED_MENU = "app/nestedMenu/CLOSE_NESTED_MENU";
const RESET_CLOSE_NESTED_MENU = "app/nestedMenu/CLOSE_NESTED_MENU";
const SET_NESTED_FORM_MODULE_DATA =
  "app/nestedMenu/SET_NESTED_FORM_MODULE_DATA";
const CLEAR_NESTED_FORM_MODULE_DATA =
  "app/nestedMenu/CLEAR_NESTED_FORM_MODULE_DATA";
const SET_RELOAD_NESTED_FORM_PARENT_FORM_TOGGLE =
  "app/nestedMenu/SET_RELOAD_NESTED_FORM_PARENT_FORM_TOGGLE";
const SET_CREATED_DATA_PARENT_FIELD_DATA =
  "app/nestedMenu/SET_CREATED_DATA_PARENT_FIELD_DATA";
const SET_NESTED_PARENT_FORM_DATA =
  "app/nestedMenu/SET_NESTED_PARENT_FORM_DATA";
// Action types for nested form creation context
const SET_NESTED_FORM_CREATION_CONTEXT = "app/nestedMenu/SET_NESTED_FORM_CREATION_CONTEXT";
const CLEAR_NESTED_FORM_CREATION_CONTEXT = "app/nestedMenu/CLEAR_NESTED_FORM_CREATION_CONTEXT";

type Action =
  | {
      type: typeof CLOSE_NESTED_MENU;
      payload: boolean;
    }
  | { type: typeof RESET_CLOSE_NESTED_MENU; payload: boolean }
  | { type: typeof CLEAR_NESTED_FORM_MODULE_DATA }
  | { type: typeof SET_NESTED_FORM_MODULE_DATA; payload: NestedFormModuleData }
  | {
      type: typeof SET_RELOAD_NESTED_FORM_PARENT_FORM_TOGGLE;
      payload: boolean;
    }
  | {
      type: typeof SET_CREATED_DATA_PARENT_FIELD_DATA;
      payload: CreatedDataParentFieldData | null;
    }
  | {
      type: typeof SET_NESTED_PARENT_FORM_DATA;
      payload: NestedParentFormData | null;
    }
  | {
      type: typeof SET_NESTED_FORM_CREATION_CONTEXT; payload: NonNullable<State["nestedFormCreationContext"]>;
    }
  | {
      type: typeof CLEAR_NESTED_FORM_CREATION_CONTEXT;
    };

/* REDUCER */

export default function reducer(state = initialState, action: Action): State {
  return produce(state, draft => {
    switch (action.type) {
      case CLOSE_NESTED_MENU:
        draft.closeNestedMenu = action.payload;
        break;
      case RESET_CLOSE_NESTED_MENU:
        draft.closeNestedMenu = action.payload;
        break;
      case SET_NESTED_FORM_MODULE_DATA:
        draft.nestedFormModuleData = action.payload;
        break;
      case CLEAR_NESTED_FORM_MODULE_DATA:
        draft.nestedFormModuleData = null;
        break;
      case SET_RELOAD_NESTED_FORM_PARENT_FORM_TOGGLE:
        draft.reloadNestedFormParentFormToggle = action.payload;
        break;
      case SET_CREATED_DATA_PARENT_FIELD_DATA:
        draft.createdDataParentFieldData = action.payload;
        break;
      case SET_NESTED_PARENT_FORM_DATA:
        draft.nestedParentFormData = action.payload;
        break;
      case SET_NESTED_FORM_CREATION_CONTEXT:
        draft.nestedFormCreationContext = action.payload;
        break;
      case CLEAR_NESTED_FORM_CREATION_CONTEXT:
        draft.nestedFormCreationContext = null;
        break;
      default:
        break;
    }
  });
}

export const setCloseNestedMenu = (): Action => ({
  type: CLOSE_NESTED_MENU,
  payload: true,
});

export const setResetCloseNestedMenu = (): Action => ({
  type: RESET_CLOSE_NESTED_MENU,
  payload: false,
});

export const setNestedFormModuleData = (
  payload: NestedFormModuleData
): Action => ({
  type: SET_NESTED_FORM_MODULE_DATA,
  payload,
});

export const clearNestedFormModuleData = (): Action => ({
  type: CLEAR_NESTED_FORM_MODULE_DATA,
});

export const setReloadNestedFormParentFormToggle = (
  payload: boolean
): Action => ({
  type: SET_RELOAD_NESTED_FORM_PARENT_FORM_TOGGLE,
  payload,
});

export const setCreatedDataParentFieldData = (
  payload: CreatedDataParentFieldData | null
): Action => ({
  type: SET_CREATED_DATA_PARENT_FIELD_DATA,
  payload,
});

export const setNestedParentFormData = (
  payload: NestedParentFormData | null
): Action => ({
  type: SET_NESTED_PARENT_FORM_DATA,
  payload,
});

export const setNestedFormCreationContext = (
  payload: NonNullable<State["nestedFormCreationContext"]>
): Action => ({
  type: SET_NESTED_FORM_CREATION_CONTEXT,
  payload,
});

export const clearNestedFormCreationContext = (): Action => ({
  type: CLEAR_NESTED_FORM_CREATION_CONTEXT,
});

/* SELECTORS */

export const selectCloseNestedMenu = (app: Store) =>
  app.nestedMenu.closeNestedMenu;

export const selectNestedFormModuleData = (app: Store) =>
  app.nestedMenu.nestedFormModuleData;

export const selectReloadNestedFormParentFormToggle = (app: Store) =>
  app.nestedMenu.reloadNestedFormParentFormToggle;

export const selectCreatedDataParentFieldData = (app: Store) =>
  app.nestedMenu.createdDataParentFieldData;

export const selectNestedParentFormData = (app: Store) =>
  app.nestedMenu.nestedParentFormData;

export const selectNestedFormCreationContext = (app: Store) =>
  app.nestedMenu.nestedFormCreationContext;
